import {
  signalStore,
  withState,
  patchState,
  withMethods,
  withProps,
  withComputed,
} from '@ngrx/signals';
import {
  ImpersonationService,
  ImpersonationTokenResponse,
} from '../data/impersonation.service';
import { withCalls, withSyncToWebStorage } from '@ngrx-traits/signals';
import { computed, inject } from '@angular/core';
import { typedCallConfig } from '@ngrx-traits/signals';
import { ToastService } from '@core/toast';
import { UserRightsStore } from '@core/user-rights';
import { ImpersonationUser } from '../models/impersonation-user.model';
import { decodeIdToken } from '../models/decoded-id-token-payload.model';
import { withLogger } from '@ngrx-traits/signals';

interface ImpersonationState {
  refreshToken: string | null;
  accessToken: string | null;
  idToken: string | null;
}

const initialState: ImpersonationState = {
  refreshToken: null,
  accessToken: null,
  idToken: null,
};

export const ImpersonationStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),

  withComputed(({ idToken, accessToken }) => ({
    isImpersonated: computed(() => accessToken() !== null),
    impersonatedUserData: computed(() => decodeIdToken(idToken())),
  })),
  withComputed(({ impersonatedUserData }) => ({
    impersonatedUserId: computed(
      () => impersonatedUserData()?.sub && parseInt(impersonatedUserData()!.sub)
    ),
  })),
  withSyncToWebStorage({
    key: 'impersonationTokens',
    type: 'local',
    restoreOnInit: true,
    saveStateChangesAfterMs: 0,
    filterState: ({ refreshToken, accessToken, idToken }) => ({
      refreshToken,
      accessToken,
      idToken,
    }),
  }),
  withLogger({
    name: 'ImpersonationStore',
    showDiff: true,
  }),
  withProps(() => ({
    _impersonationService: inject(ImpersonationService),
    _toastService: inject(ToastService),
    _userRightsStore: inject(UserRightsStore),
  })),
  withMethods(
    ({ saveToStorage, clearFromStore, _userRightsStore, ...state }) => ({
      setImpersonationTokens(tokens: ImpersonationTokenResponse) {
        patchState(state, {
          refreshToken: tokens.refreshToken,
          accessToken: tokens.accessToken,
          idToken: tokens.idToken,
        });
        saveToStorage();
      },
      clearImpersonationTokens() {
        patchState(state, {
          refreshToken: null,
          accessToken: null,
          idToken: null,
        });
        clearFromStore();
        _userRightsStore.getUserRights();
      },
    })
  ),
  withCalls(
    ({
      _impersonationService,
      _toastService,
      _userRightsStore,
      setImpersonationTokens,
    }) => ({
      impersonate: typedCallConfig({
        call: (user: ImpersonationUser) =>
          _impersonationService.impersonateUser(String(user.id)),
        mapError: () => 'Fehler beim Impersionieren',
        onError: (error) => _toastService.error(String(error), ''),
        onSuccess: (result, param) => {
          setImpersonationTokens(result);
          _userRightsStore.getUserRights();
          _toastService.success(
            'Erfolgreich impersoniert als ' + param.email,
            ''
          );
        },
      }),
    })
  )
);
